import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/axiosConfig";
import {
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import PasswordInput from "./PasswordInput";
import "./RegisterPages.css";

const RegisterRelationshipManager = () => {
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "Aionion@123",
    reports_to_id: "",
    reports_to_role: "",
    email: "",
    location: "",
  });

  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const navigate = useNavigate();
  const { teamLeadId } = useParams();

  useEffect(() => {
    if (teamLeadId) {
      // If teamLeadId is provided, set it as reports_to_id and role as team_lead
      setFormData((prev) => ({
        ...prev,
        reports_to_id: Number(teamLeadId),
        reports_to_role: "team_lead",
      }));
      setSelectedUser({
        id: Number(teamLeadId),
        name: "Pre-selected Team Lead",
        username: "",
      }); // Placeholder, might need to fetch name
    }
  }, [teamLeadId]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (e) => {
    const role = e.target.value;
    setSelectedRole(role);
    // For directors, automatically set location to "all" since they can be assigned regardless of location
    setSelectedLocation(role === "director" ? "all" : "");
    setUsers([]);
    setSelectedUser(null);
    setFormData({
      ...formData,
      reports_to_id: "",
      reports_to_role: role,
    });
  };

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  const fetchUsers = async () => {
    if (!selectedRole || !selectedLocation) return;
    setLoading(true);
    try {
      const response = await api.get(
        `/api/admin/users?role=${selectedRole}&location=${selectedLocation}`
      );
      setUsers(response.data || []);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage("Could not load users list. Please try again.");
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      reports_to_id: user.id,
      reports_to_role: selectedRole,
    });
    setModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    setIsError(false);

    try {
      const response = await api.post(
        "/api/admin/register/relationship_manager",
        formData
      );

      setMessage(
        response.data.message || "Relationship Manager registered successfully"
      );
      setIsError(false);

      setFormData({
        name: "",
        username: "",
        password: "Aionion@123",
        reports_to_id: "",
        reports_to_role: "",
        email: "",
        location: "",
      });
      setSelectedRole("");
      setSelectedLocation("");
      setSelectedUser(null);

      setTimeout(() => navigate("/admin-dashboard"), 2000);
    } catch (err) {
      console.error(err);
      setMessage(
        err.response?.data?.message || "Error registering Relationship Manager"
      );
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-form">
      <h2>Register Relationship Manager</h2>

      {message && (
        <div className={`alert ${isError ? "alert-danger" : "alert-success"}`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="name" className="form-label">
            Name
          </label>
          <input
            type="text"
            className="form-control"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="username" className="form-label">
            Username
          </label>
          <input
            type="text"
            className="form-control"
            id="username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <PasswordInput
          label="Password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          disabled={loading}
        />

        <div className="mb-3">
          <label htmlFor="email" className="form-label">
            Email
          </label>
          <input
            type="email"
            className="form-control"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="location" className="form-label">
            Location
          </label>
          <select
            className="form-control"
            id="location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select Location</option>
            <option value="Chennai">Chennai</option>
            <option value="Trichy">Trichy</option>
            <option value="Coimbatore">Coimbatore</option>
            <option value="Bangalore">Bangalore</option>
            <option value="Dubai">Dubai</option>
            <option value="Singapore">Singapore</option>
            <option value="Madurai">Madurai</option>
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Assign Reports To</label>
          <div className="row">
            <div className="col-md-4">
              <select
                className="form-control"
                value={selectedRole}
                onChange={handleRoleChange}
                disabled={loading || !!teamLeadId}
              >
                <option value="">Select Role</option>
                <option value="team_lead">Team Lead</option>
                <option value="branch_manager">Branch Manager</option>
                <option value="zonal_head">Zonal Head</option>
                <option value="director">Director</option>
              </select>
            </div>
            <div className="col-md-4">
              <select
                className="form-control"
                value={selectedLocation}
                onChange={handleLocationChange}
                disabled={
                  loading ||
                  !selectedRole ||
                  selectedRole === "director" ||
                  !!teamLeadId
                }
              >
                <option value="">Select Location</option>
                {selectedRole === "director" ? (
                  <option value="all">All</option>
                ) : (
                  <>
                    <option value="Chennai">Chennai</option>
                    <option value="Trichy">Trichy</option>
                    <option value="Coimbatore">Coimbatore</option>
                    <option value="Bangalore">Bangalore</option>
                    <option value="Dubai">Dubai</option>
                    <option value="Singapore">Singapore</option>
                    <option value="Madurai">Madurai</option>
                  </>
                )}
              </select>
            </div>
            <div className="col-md-4">
              <Button
                variant="outlined"
                onClick={fetchUsers}
                disabled={
                  loading || !selectedRole || !selectedLocation || !!teamLeadId
                }
                startIcon={<Search />}
              >
                Search
              </Button>
            </div>
          </div>
          {selectedUser && (
            <div className="mt-2">
              <TextField
                fullWidth
                value={`${selectedUser.name} (${selectedUser.username})`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSelectedUser(null);
                          setFormData({
                            ...formData,
                            reports_to_id: "",
                            reports_to_role: "",
                          });
                        }}
                      >
                        <Close />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled
              />
            </div>
          )}
        </div>

        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Select User</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              margin="dense"
            />
            <List>
              {users
                .filter(
                  (user) =>
                    user.name
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    user.username
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                )
                .map((user) => (
                  <ListItem
                    key={user.id}
                    onClick={() => handleUserSelect(user)}
                    sx={{ cursor: "pointer" }}
                  >
                    <ListItemText primary={`${user.name} (${user.username})`} />
                  </ListItem>
                ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>

        <div className="btn-container">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Registering..." : "Register Relationship Manager"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/admin-dashboard")}
            disabled={loading}
          >
            Back to Dashboard
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegisterRelationshipManager;
